import { BadgeOutlined, CheckCircleRounded, CloseOutlined, DoNotDisturbOnRounded, DriveFileRenameOutlineOutlined, KeyOutlined, RemoveCircleOutlineOutlined, SaveOutlined, SevenMpOutlined, TouchAppOutlined } from "@mui/icons-material";
import { Autocomplete, Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, Paper, Skeleton, styled, TextField, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { SetterOrUpdater, useRecoilValue } from "recoil";
import { CommandRequestType, CommandType, SelectedParametersType, UserType } from "../../../types/types";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { userAtom } from "../../../atoms/atoms";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { ContentProps, Toast } from "primereact/toast";

import { getFilteredList, setToastRefToOpen } from "../../../functions/functions";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view";
import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";

import findAllDevicesProtocolsByManufacturerIdListResponse from "../../../resources/Api/device/findAllDevicesProtocolsByManufaturerIdListResponse";
import findAllDeviceManufacturersListResponse from "../../../resources/Api/device/findAllDeviceManufaturersListResponse";
import commandfindAllByActivated from "../../../resources/Api/command/commandfindAllByActivated";
import commandOnSaveData from "../../../resources/Api/command/commandOnSaveData";
import commandOnUpdateData from "../../../resources/Api/command/commandOnUpdateData";
import commandUpdateStatusById from "../../../resources/Api/command/commandUpdateStatusById";
import commandFindAllExportResponse from "../../../resources/Api/command/commandFindAllExportResponse";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "description",
        align: "left",
        width: 200,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Comando"}
                icon={
                    <TouchAppOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.description}
            />
        ),
    },
    {
        field: "protocolName",
        align: "left",
        width: 200,
        headerClassName: "last-column",
        editable: false,
         sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Protocolo"}
                icon={
                    <SevenMpOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.protocolName || "Geral"}
            />
        ),
    }
];

const rowsOptions = (
    list: Array<CommandType>,
    openDetails?: SetterOrUpdater<boolean>
): Array<CommandType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, openDetails }));
};

/**
 * CommandPage - Screen that renders a page to create and manage the protocols commands
 * @component-screen
 */
const CommandPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<CommandType>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // states
    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    const [deviceManufacturersList, setDeviceManufacturersList] = useState<Array<{ id: number, name: string }>>([]);
    const [manufacturerProtocolsList, setManufacturerProtocolsList] = useState<Array<{ id: number, name: string, manufacturerId: number }>>([]);

    const [deviceManufacturerName, setDeviceManufacturerName] = useState<string>("");
    const [manufacturerProtocolName, setManufacturerProtocolName] = useState<string>("");

    const [commandsList, setCommandsList] = useState<Array<CommandType | any>>([]);
    const [description, setDescription] = useState<string>("");
    const [attributes, setAttributes] = useState<string>("");
    
    const [selectedCommand, setSelectedCommand] = useState<CommandType>({
        id: -1,
        description: "",
        type: "",
        attributes: "",
        protocolId: -1,
        protocolName: "",
        activated: true,
    });

    // refs
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);
    const location = useLocation();

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setSelectedCommand({
            id: -1,
            description: "",
            type: "",
            attributes: "",
            protocolId: -1,
            protocolName: "",
            activated: true
        });
        setInEdit(false);
        setInInsert(false);
        setDescription("");
        setAttributes("");
        setDeviceManufacturerName("");
        setManufacturerProtocolName("");
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const getAllCommands = useCallback(
        async (search: string = "", status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const currentParameters: SelectedParametersType = cookies.selectedParameters;

            setLoading(true);
            try {
                const { data } = await commandfindAllByActivated(
                    token,
                    currentParameters.customer.id,
                    status,
                    page,
                    size
                );
                setCommandsList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setCommandsList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token, cookies.selectedParameters]
    );

    useEffect(() => {
        getAllCommands();
    }, [getAllCommands]);

    const getDeviceManufacturersList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);
        try {
            const { data } = await findAllDeviceManufacturersListResponse(token);
            setDeviceManufacturersList(data);
        } catch (error: any) {
            console.log(error);
            setDeviceManufacturersList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.token]);

    useEffect(() => {
        getDeviceManufacturersList();
    }, [getDeviceManufacturersList]);

    const getProtocolsList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const manufacturerId = deviceManufacturersList.find((item) => { if (item.name === deviceManufacturerName) return item })?.id;

        try {
            const { data } = await findAllDevicesProtocolsByManufacturerIdListResponse(
                token,
                manufacturerId!!
            );
            setManufacturerProtocolsList(data);
            setManufacturerProtocolName(data.find((item: any) => item.id === selectedCommand.protocolId)?.name ?? "");
        } catch (error: any) {
            console.log(error);
            setManufacturerProtocolsList([]);
        }
    }, [cookies.token, inEdit, deviceManufacturerName, selectedCommand, deviceManufacturersList]);

    useEffect(() => {
        if (deviceManufacturerName) {
            getProtocolsList();
        }
    }, [getProtocolsList, deviceManufacturerName]);

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);
        const dataToSave: CommandRequestType = {
            id: -1,
            type: "custom",
            description: description,
            attributes: JSON.stringify({ data: attributes }) || "",
            protocolId: manufacturerProtocolsList.find((it) => it.name === manufacturerProtocolName)?.id!!,
            managerId: currentParameters.customer.id
        };

        if (
            !dataToSave.description ||
            !dataToSave.attributes ||
            !dataToSave.protocolId
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await commandOnSaveData(token, dataToSave);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllCommands();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        description,
        manufacturerProtocolName,
        manufacturerProtocolsList,
        attributes,
        cookies.selectedParameters,
        cookies.token,
        handleClose,
        getAllCommands
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);
        const dataToSave: CommandRequestType = {
            id: selectedCommand.id,
            type: selectedCommand.type,
            description: description,
            attributes: JSON.stringify({ data: attributes }) || "",
            protocolId: manufacturerProtocolsList.find((it) => it.name === manufacturerProtocolName)?.id!!,
            managerId: currentParameters.customer.id
        };

        if (
            !dataToSave.description ||
            !dataToSave.attributes ||
            !dataToSave.protocolId
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        const teste = JSON.parse(selectedCommand?.attributes) ?? ""
        if (
            description === selectedCommand.description &&
            attributes === teste.data &&
            manufacturerProtocolsList.find((it) => it.name === manufacturerProtocolName)?.id!! === selectedCommand.protocolId
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await commandOnUpdateData(token, dataToSave, dataToSave.id);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllCommands();
            }, 3000);

        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        description,
        manufacturerProtocolName,
        manufacturerProtocolsList,
        selectedCommand,
        attributes,
        cookies.selectedParameters,
        cookies.token,
        handleClose,
        getAllCommands,
    ]);

    const verifyBeforeCancel = useCallback(
        (selected: CommandType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                const teste = JSON.parse(selectedCommand?.attributes) ?? ""

                if (
                    description !== selected.description ||
                    attributes !== teste.data ||
                    manufacturerProtocolsList.find((it) => it.name === manufacturerProtocolName)?.id!! !== selected.protocolId
                ) {
                    canCancel = false;
                }
            } else if (!inEdit && inInsert && (description || manufacturerProtocolName || attributes)) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            isToastVisible,
            description,
            attributes,
            manufacturerProtocolName,
            manufacturerProtocolsList,
            selectedCommand?.attributes
        ]
    );

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await commandUpdateStatusById(
                    token,
                    id,
                    status
                );
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response;

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllCommands();
                }, 8000);
            }
        },
        [cookies.token, cookies.selectedParameters, getAllCommands, handleClose]
    );

    const getReport = useCallback(async (type: string) => {
        setLoading(true);
        let dataBuffer = new ArrayBuffer(0);
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        try {
            const { data } = await commandFindAllExportResponse(cookies.token,
                currentParameters.customer.id,
                (commandsList.length > 0 && commandsList[0].activated)
                    ? "activated"
                    : "deactivated", reportType, isSimpleType)

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, commandsList, cookies.selectedParameters, handleClose]);

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const handleOnEditValues = useCallback(async (selectedCommand: CommandType) => {
        if (selectedCommand?.type && !inInsert) {
            setDescription(selectedCommand.description)
            setAttributes(JSON.parse(selectedCommand.attributes).data ?? "")
            setDeviceManufacturerName(deviceManufacturersList.find((item) => item.id === selectedCommand.protocolDeviceManufacturerId)?.name ?? "");
        }
    }, [
        inInsert,
        deviceManufacturersList
    ]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 650,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 650, padding: 5 }}>
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton variant="rectangular" width={"100%"} height={350} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const CustomTreeItem = styled(TreeItem)({
        [`& .${treeItemClasses.iconContainer}`]: {
            "& .close": {
                opacity: 0.3,
            },
        },
    });

    useEffect(() => {
        setFilteredList(getFilteredList(["description", "type"], commandsList, query));
    }, [commandsList, query]);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="command-form-surface tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth dark:tw-bg-slate-900"
                    sx={style}
                >
                    <Box
                        className="command-form-surface row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6 dark:tw-bg-slate-900"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: "#ffff",
                                transition: "box-shadow 200ms ease, background-color 200ms ease",
                            },
                            "& .MuiOutlinedInput-root:hover": {
                                backgroundColor: "rgba(0, 108, 119, 0.02)",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused": {
                                boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#006C77",
                            },
                        }}
                    >
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Identificação
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                >
                                    {selectedCommand?.description && (
                                        <>
                                            <Chip
                                                className="command-action-chip"
                                                style={{
                                                    color: selectedCommand.activated
                                                        ? "#00B700"
                                                        : "#dc3545",
                                                    marginRight: 5,
                                                    width: "50%",
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={selectedCommand.activated ? "Ativo" : "Inativo"}
                                            />
                                            {selectedCommand.activated && (
                                                <Chip
                                                    className="command-action-chip"
                                                    style={{
                                                        color: "black",
                                                        marginRight: 5,
                                                        width: "50%",
                                                    }}
                                                    disabled={!location?.state?.canEdit}
                                                    onClick={() => {
                                                        setInEdit(true);
                                                    }}
                                                    label="Editar"
                                                    icon={
                                                        <DriveFileRenameOutlineOutlined
                                                            style={{ height: 24, width: 24, fill: "black" }}
                                                        />
                                                    }
                                                />
                                            )}
                                            <Chip
                                                className="command-action-chip"
                                                style={{
                                                    color: "black",
                                                    marginRight: 5,
                                                    width: "50%",
                                                }}
                                                disabled={!location?.state?.canEdit}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(
                                                            selectedCommand.id,
                                                            selectedCommand.activated
                                                                ? "deactivate"
                                                                : "activate"
                                                        );
                                                    }
                                                }}
                                                label={
                                                    selectedCommand.activated ? "Desativar" : "Ativar"
                                                }
                                                icon={
                                                    <RemoveCircleOutlineOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />
                                        </>
                                    )}
                                </Box>
                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 15px 0px" }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-password-label"
                                            required
                                            shrink
                                            disabled={
                                                (selectedCommand?.description &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            Nome
                                        </InputLabel>
                                        <TextField
                                            name="name"
                                            required
                                            autoComplete="off"
                                            InputProps={{
                                                label: "Nome",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={
                                                (selectedCommand?.description &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            value={description}
                                            onChange={(event) => {
                                                setDescription(event.target.value)
                                            }}
                                            type="text"
                                            className="form-control form-control-login"
                                            placeholder="Digite o nome"
                                        />
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            className="visibility-password"
                                            disabled
                                            onMouseDown={(e: SyntheticEvent) => { }}
                                            edge="end"
                                            size="large"
                                        >
                                            <BadgeOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>
                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 15px 0px" }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-password-label"
                                            required
                                            shrink
                                            disabled={
                                                (selectedCommand?.description &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            comando
                                        </InputLabel>
                                        <TextField
                                            name="name"
                                            required
                                            autoComplete="off"
                                            InputProps={{
                                                label: "comando",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={
                                                (selectedCommand?.description &&
                                                    !inEdit &&
                                                    !inInsert) as boolean
                                            }
                                            value={attributes}
                                            onChange={(event) => {
                                                setAttributes(event.target.value)
                                            }}
                                            type="text"
                                            className="form-control form-control-login"
                                            placeholder="Digite o comando"
                                        />
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            className="visibility-password"
                                            disabled
                                            onMouseDown={(e: SyntheticEvent) => { }}
                                            edge="end"
                                            size="large"
                                        >
                                            <BadgeOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>

                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 20px 0px" }}
                                >
                                    <Autocomplete
                                        id="chip-label-select-country"
                                        className="input-select-country"
                                        style={{
                                            width: "100%",
                                            minHeight: "56px",
                                            height: "56px",
                                            backgroundColor: "#ffffff29",
                                            borderColor: "lightgray",
                                        }}
                                        options={deviceManufacturersList}
                                        value={deviceManufacturersList.find((a) => a.name === deviceManufacturerName) ?? null}
                                        onChange={(_, option) => {
                                            setDeviceManufacturerName(option?.name ?? "");
                                        }}
                                        getOptionLabel={(option) => `${option.name}`}
                                        noOptionsText={"Nada encontrado"}
                                        renderInput={(params) => (
                                            <>
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex" }}
                                                >
                                                    <InputLabel
                                                        id="country-label"
                                                        required
                                                        style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                        shrink
                                                        disabled={
                                                            (selectedCommand?.description &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        Fabricante
                                                    </InputLabel>
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Selecione um fabricante"
                                                        autoComplete="off"
                                                        required
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            notched: true,
                                                            label: "Fabricante",
                                                            classes: {
                                                                notchedOutline: "custom-notch",
                                                            },
                                                        }}
                                                    />
                                                </FormControl>
                                            </>
                                        )}
                                        disabled={
                                            (selectedCommand?.description &&
                                                !inEdit &&
                                                !inInsert) as boolean
                                        }
                                    />
                                </Box>

                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 20px 0px" }}
                                >
                                    <Autocomplete
                                        id="chip-label-select-protocol"
                                        className="input-select-protocol"
                                        style={{
                                            width: "100%",
                                            minHeight: "56px",
                                            height: "56px",
                                            backgroundColor: "#ffffff29",
                                            borderColor: "lightgray",
                                        }}
                                        options={manufacturerProtocolsList}
                                        value={manufacturerProtocolsList.find((a) => a.name === manufacturerProtocolName) ?? null}
                                        onChange={(_, option) => {
                                            setManufacturerProtocolName(option?.name ?? "");
                                        }}
                                        getOptionLabel={(option) => `${option.name}`}
                                        noOptionsText={"Nada encontrado"}
                                        renderInput={(params) => (
                                            <>
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex" }}
                                                >
                                                    <InputLabel
                                                        id="country-label"
                                                        required
                                                        style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                        shrink
                                                        disabled={
                                                            (selectedCommand?.description &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        Protocolo
                                                    </InputLabel>
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Selecione um protocolo"
                                                        autoComplete="off"
                                                        required
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            notched: true,
                                                            label: "Protocolo",
                                                            classes: {
                                                                notchedOutline: "custom-notch",
                                                            },
                                                        }}
                                                    />
                                                </FormControl>
                                            </>
                                        )}
                                        disabled={
                                            (selectedCommand?.description &&
                                                !inEdit &&
                                                !inInsert) as boolean
                                        }
                                    />
                                </Box>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{
                            display: "flex",
                            width: "100%",
                            alignSelf: "stretch",
                            justifyContent: "flex-end",
                            padding: 2,
                            borderTop: "1px solid rgba(0, 0, 0, 0.10)",
                            backgroundColor: "#ffff",
                        }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedCommand);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        CustomTreeItem,
        location?.state?.canEdit,
        isToastVisible,
        validationText,
        isOpenDetails,
        loading,
        loadingEsqueleton,
        inInsert,
        inEdit,
        description,
        deviceManufacturerName,
        deviceManufacturersList,
        manufacturerProtocolName,
        onSave,
        onUpdate,
        selectedCommand,
        attributes,
        manufacturerProtocolsList,
        verifyBeforeUpdateStatus,
        verifyBeforeCancel
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Comandos"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, (item: any) => {
                    setIsOpenDetails(true);
                    setSelectedCommand(item);
                })}
                sortField="description"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Descrição ou tipo"}
                onRefresh={getAllCommands}
                onExport={getReport}
                disableAddButton={!location?.state?.canCreate}
                handleNext={(activated: string, page: number, size: number) => getAllCommands("", activated, page, size)}
                handlePrevious={() => {}}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedCommand(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(92vw, 760px)" },
    maxWidth: 760,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default CommandPage;
